import { Outlet, useLocation } from 'react-router'
import { Sidebar } from './Sidebar'
import { useState, useEffect, Suspense, lazy, useMemo } from 'react'
import PageLoader from '@/components/common/PageLoader'
import QueryEditorErrorBoundary from '@/components/query/QueryEditorErrorBoundary'
import { useAgentHealthMonitor } from '@/hooks/useAgentHealthMonitor'
import { useVersionCheck } from '@/hooks/useVersionCheck'
import { useUIStore } from '@/stores/uiStore'

// Lazy-load QueryEditor for code splitting (loaded once, kept alive)
const QueryEditor = lazy(async () => ({ default: (await import('@/pages/QueryEditor')).QueryEditor }))

export function MainLayout() {
  // Single mount point for the connection/agent health monitor. Runs for
  // as long as the authenticated shell is mounted so poll + reactive re-checks
  // keep the active connection state honest.
  useAgentHealthMonitor()
  useVersionCheck()

  const updateAvailable = useUIStore((s) => s.updateAvailable)

  const location = useLocation()
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('sidebar-collapsed')
    try {
      const parsed = saved ? JSON.parse(saved) as unknown : false
      return typeof parsed === 'boolean' ? parsed : false
    } catch {
      return false
    }
  })
  const [isMobile, setIsMobile] = useState(false)

  // Detect query route and extract connectionId
  const queryRouteMatch = useMemo(() => /^\/query\/(.+)/.exec(location.pathname), [location.pathname])
  const isQueryRoute = !!queryRouteMatch
  const queryConnectionId = queryRouteMatch?.[1] || ''

  // Track if QueryEditor has ever been mounted (lazy mount on first visit)
  const [queryEditorMounted, setQueryEditorMounted] = useState(false)
  // Track the latest connectionId for the keep-alive QueryEditor
  const [activeQueryConnectionId, setActiveQueryConnectionId] = useState('')

  useEffect(() => {
    if (isQueryRoute && queryConnectionId) {
      setQueryEditorMounted(true)
      setActiveQueryConnectionId(queryConnectionId)
    }
  }, [isQueryRoute, queryConnectionId])

  // Check if we're on mobile and auto-collapse sidebar on mobile
  useEffect(() => {
    const checkMobile = () => {
      const isMobileScreen = window.innerWidth < 768
      setIsMobile(isMobileScreen)

      // Auto-collapse sidebar on mobile screens for better space utilization
      if (isMobileScreen) {
        setSidebarCollapsed(true)
      }
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => { window.removeEventListener('resize', checkMobile) }
  }, [])

  // Save sidebar collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(sidebarCollapsed))
  }, [sidebarCollapsed])

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  return (
    <div className="flex h-full bg-gray-50 dark:bg-gray-900">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        isMobile={isMobile}
      />

      <div className="flex-1 flex flex-col min-h-0 min-w-0">
        {updateAvailable && (
          <div
            role="status"
            className="flex items-center justify-between gap-3 px-4 py-2 text-sm bg-blue-600 text-white"
          >
            <span>A new version is available. Reload to update.</span>
            <button
              type="button"
              onClick={() => { window.location.reload() }}
              className="px-3 py-1 rounded bg-white/15 hover:bg-white/25 transition-colors font-medium"
            >
              Reload
            </button>
          </div>
        )}
        <main className="flex-1 overflow-auto">
          {/* Regular routes via Outlet - hidden when on query route */}
          <div style={{ display: isQueryRoute ? 'none' : undefined }} className="h-full">
            <Outlet />
          </div>

          {/* Keep-alive QueryEditor - mounts on first visit, stays alive via CSS display toggle */}
          {queryEditorMounted && (
            <div style={{ display: isQueryRoute ? undefined : 'none' }} className="h-full">
              <Suspense fallback={<PageLoader />}>
                <QueryEditorErrorBoundary>
                  <QueryEditor connectionId={activeQueryConnectionId} isActive={isQueryRoute} />
                </QueryEditorErrorBoundary>
              </Suspense>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}
